import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    AdComment,
    AdCommentPin,
    AdFileKind,
    AdPinKind,
    AdPinRect,
    adReviewComments
} from '../../marketing-review-interfaces/marketing-review.interface';
import { DocxRenderer } from './renderers/docx-renderer';
import { PageRenderer, RenderedPage } from './renderers/page-renderer';
import { PdfRenderer } from './renderers/pdf-renderer';
import { PptxRenderer } from './renderers/pptx-renderer';
import { SheetRenderer } from './renderers/sheet-renderer';
import { ImageRenderer, TextRenderer } from './renderers/simple-renderers';

/** A pin the reviewer has just placed, before it is posted. The page adds the attachment. */
export type PinDraft = Omit<AdCommentPin, 'documentId'>;

/** Extensions with an in-browser renderer. Legacy `.doc` and `.ppt` are deliberately absent. */
const RENDERER_BY_EXTENSION: Record<string, () => PageRenderer> = {
    pdf: () => new PdfRenderer(),
    docx: () => new DocxRenderer(),
    pptx: () => new PptxRenderer(),
    xlsx: () => new SheetRenderer(),
    xls: () => new SheetRenderer(),
    csv: () => new SheetRenderer(),
    txt: () => new TextRenderer(),
};

/** Longest quote kept on a text pin — the card shows a line or two, not a paragraph dump. */
const MAX_QUOTE_LENGTH: number = 300;

/** A drag smaller than this (as a fraction of the page) is a click, not an area. */
const MIN_AREA: number = 0.01;

function extensionOf(fileName: string): string {
    return fileName.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
}

function rendererFor(fileName: string, kind: AdFileKind): (() => PageRenderer) | null {
    if (kind === 'image') {
        return () => new ImageRenderer();
    }
    return RENDERER_BY_EXTENSION[extensionOf(fileName)] ?? null;
}

/** True when the viewer can render this file, so the page knows whether to fall back to a card. */
export function pinViewerSupports(fileName: string, kind: AdFileKind): boolean {
    return rendererFor(fileName, kind) !== null;
}

/**
 * Renders a material file in the browser and lets a reviewer pin comments to it — by highlighting
 * text or by dragging a box.
 *
 * The file is drawn by a per-format renderer into plain page elements; this component lays an
 * overlay over each page and owns everything pin-related, so pins behave the same for a PDF, a
 * Word page, a slide, a worksheet or an image. Pins are stored as fractions of their page, so
 * they stay put through zoom.
 *
 * The renderers write DOM that Angular does not own, which is why styles here are not
 * encapsulated — every rule is nested under `app-pin-viewer` instead, so nothing leaks.
 */
@Component({
    selector: 'app-pin-viewer',
    imports: [MatIconModule, MatTooltipModule],
    templateUrl: './pin-viewer.component.html',
    styleUrl: './pin-viewer.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class PinViewerComponent implements OnChanges, OnDestroy {

    @Input({ required: true }) src!: string;
    @Input({ required: true }) fileName!: string;
    @Input({ required: true }) kind!: AdFileKind;
    /** Percent; 100 fits the pane. */
    @Input() zoom: number = 100;
    /** Pinned comments on this file. */
    @Input() pins: adReviewComments[] = [];
    @Input() activePinId: number | null = null;
    /** The pin waiting in the composer, drawn dashed until it is posted. */
    @Input() draft: PinDraft | null = null;
    @Input() readOnly: boolean = false;

    @Output() pinDraft = new EventEmitter<PinDraft>();
    @Output() pinSelect = new EventEmitter<number>();

    @ViewChild('host', { static: true }) private hostRef!: ElementRef<HTMLElement>;

    protected loading: boolean = false;
    protected error: string = '';
    protected mode: AdPinKind = 'text';
    protected hasText: boolean = false;
    protected paged: boolean = false;
    protected pages: RenderedPage[] = [];
    protected activePage: number = 0;

    private renderer: PageRenderer | null = null;
    private overlays: HTMLElement[] = [];
    /** Bumped on every load, so a slow earlier file never paints over the current one. */
    private loadToken: number = 0;
    /** A pin asked for before its file finished rendering. */
    private pendingFocus: number | null = null;

    /** The in-progress drag, while drawing an area. */
    private drag: { page: number; startX: number; startY: number; box: HTMLElement } | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['src'] || changes['fileName'] || changes['kind']) {
            this.load();
            return;
        }
        if (changes['zoom'] && this.renderer) {
            this.renderer.zoom(this.zoom);
        }
        if (changes['pins'] || changes['activePinId'] || changes['draft']) {
            this.drawPins();
        }
    }

    ngOnDestroy(): void {
        this.loadToken++;
        this.renderer?.destroy();
        this.renderer = null;
    }

    // ── Loading ─────────────────────────────────────────────────────────────────

    private async load(): Promise<void> {
        const token: number = ++this.loadToken;
        const host: HTMLElement = this.hostRef.nativeElement;

        this.renderer?.destroy();
        host.replaceChildren();
        this.pages = [];
        this.overlays = [];
        this.activePage = 0;
        this.error = '';

        const create = rendererFor(this.fileName, this.kind);
        if (!create) {
            this.error = 'This format cannot be previewed in the browser.';
            return;
        }

        const renderer: PageRenderer = create();
        this.renderer = renderer;
        this.hasText = renderer.hasText;
        this.paged = renderer.paged;
        this.mode = renderer.hasText ? 'text' : 'area';
        this.loading = true;

        try {
            const pages: RenderedPage[] = await renderer.render(this.src, host);
            if (token !== this.loadToken) {
                return;
            }

            this.overlays = pages.map((page) => {
                page.el.classList.add('pv-page');
                if (getComputedStyle(page.el).position === 'static') {
                    page.el.style.position = 'relative';
                }
                const overlay: HTMLDivElement = document.createElement('div');
                overlay.className = 'pv-overlay';
                page.el.appendChild(overlay);
                return overlay;
            });
            this.pages = pages;

            if (this.zoom !== 100) {
                await renderer.zoom(this.zoom);
            }
            this.showPage(0);
            this.drawPins();
        } catch (error) {
            if (token !== this.loadToken) {
                return;
            }
            console.error(`Failed to render ${this.fileName}:`, error);
            host.replaceChildren();
            this.pages = [];
            this.error = 'We couldn’t display this file in the browser.';
        } finally {
            if (token === this.loadToken) {
                this.loading = false;
            }
        }

        if (this.pendingFocus !== null && token === this.loadToken) {
            const id: number = this.pendingFocus;
            this.pendingFocus = null;
            this.scrollToPin(id);
        }
    }

    /** Worksheets show one at a time; everything else shows every page. */
    protected showPage(index: number): void {
        this.activePage = index;
        if (!this.paged) {
            return;
        }
        this.pages.forEach((page, i) => page.el.classList.toggle('is-hidden', i !== index));
    }

    protected setMode(mode: AdPinKind): void {
        this.mode = mode;
        window.getSelection()?.removeAllRanges();
    }

    // ── Pins ────────────────────────────────────────────────────────────────────

    private drawPins(): void {
        this.overlays.forEach((overlay) => overlay.replaceChildren());

        this.pins.forEach((comment) => {
            const pin: AdCommentPin | undefined = comment.pin;
            const overlay: HTMLElement | undefined = pin ? this.overlays[pin.page - 1] : undefined;
            if (!pin || !overlay || !pin.rects.length) {
                return;
            }

            const resolved: boolean = comment.status === 'Resolved';
            const active: boolean = comment.adId === this.activePinId;

            pin.rects.forEach((rect) => {
                const mark: HTMLElement = this.box(rect, 'pv-mark');
                mark.dataset['pin'] = String(comment.adId);
                mark.classList.add(pin.kind === 'text' ? 'is-text' : 'is-area');
                mark.classList.toggle('is-resolved', resolved);
                mark.classList.toggle('is-active', active);
                overlay.appendChild(mark);
            });

            const first: AdPinRect = pin.rects[0];
            const badge: HTMLButtonElement = document.createElement('button');
            badge.type = 'button';
            badge.className = 'pv-badge';
            badge.classList.toggle('is-resolved', resolved);
            badge.classList.toggle('is-active', active);
            badge.textContent = String(comment.pinNo ?? '');
            badge.title = comment.text;
            badge.setAttribute('aria-label', `Pinned comment ${comment.pinNo}`);
            badge.style.left = `${first.x * 100}%`;
            badge.style.top = `${first.y * 100}%`;
            badge.addEventListener('pointerdown', (event) => event.stopPropagation());
            badge.addEventListener('click', (event) => {
                event.stopPropagation();
                this.pinSelect.emit(comment.adId);
            });
            overlay.appendChild(badge);
        });

        const draftOverlay: HTMLElement | undefined = this.draft ? this.overlays[this.draft.page - 1] : undefined;
        if (this.draft && draftOverlay) {
            this.draft.rects.forEach((rect) => draftOverlay.appendChild(this.box(rect, 'pv-mark is-draft')));
        }
    }

    private box(rect: AdPinRect, className: string): HTMLElement {
        const el: HTMLDivElement = document.createElement('div');
        el.className = className;
        el.style.left = `${rect.x * 100}%`;
        el.style.top = `${rect.y * 100}%`;
        el.style.width = `${rect.w * 100}%`;
        el.style.height = `${rect.h * 100}%`;
        return el;
    }

    /** Brings a pin into view, switching worksheet first when needed. */
    scrollToPin(commentId: number): void {
        if (this.loading) {
            this.pendingFocus = commentId;
            return;
        }

        const comment: adReviewComments | undefined = this.pins.find((pin) => pin.adId === commentId);
        if (comment?.pin && this.paged && comment.pin.page - 1 !== this.activePage) {
            this.showPage(comment.pin.page - 1);
        }

        // After the active state is drawn, so the highlighted box is the one scrolled to.
        setTimeout(() => {
            const mark: HTMLElement | null = this.hostRef.nativeElement.querySelector(`[data-pin="${commentId}"]`);
            mark?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        });
    }

    // ── Area pins ───────────────────────────────────────────────────────────────

    protected onPointerDown(event: PointerEvent): void {
        if (this.readOnly || this.mode !== 'area' || event.button !== 0) {
            return;
        }

        const page: number = this.pageIndexOf(event.target as Node);
        if (page < 0) {
            return;
        }

        const point = this.pointOn(page, event.clientX, event.clientY);
        const box: HTMLElement = this.box({ x: point.x, y: point.y, w: 0, h: 0 }, 'pv-mark is-draft');
        this.overlays[page].appendChild(box);
        this.drag = { page, startX: point.x, startY: point.y, box };

        this.hostRef.nativeElement.setPointerCapture(event.pointerId);
        event.preventDefault();
    }

    protected onPointerMove(event: PointerEvent): void {
        if (!this.drag) {
            return;
        }
        const rect: AdPinRect = this.dragRect(event);
        Object.assign(this.drag.box.style, {
            left: `${rect.x * 100}%`,
            top: `${rect.y * 100}%`,
            width: `${rect.w * 100}%`,
            height: `${rect.h * 100}%`
        });
    }

    protected onPointerUp(event: PointerEvent): void {
        if (!this.drag) {
            return;
        }

        const rect: AdPinRect = this.dragRect(event);
        const page: number = this.drag.page;
        this.drag.box.remove();
        this.drag = null;

        if (rect.w < MIN_AREA || rect.h < MIN_AREA) {
            return;
        }

        this.pinDraft.emit({
            page: page + 1,
            pageLabel: this.pages[page].label,
            kind: 'area',
            rects: [rect]
        });
    }

    private dragRect(event: PointerEvent): AdPinRect {
        const drag = this.drag!;
        const point = this.pointOn(drag.page, event.clientX, event.clientY);
        return {
            x: Math.min(drag.startX, point.x),
            y: Math.min(drag.startY, point.y),
            w: Math.abs(point.x - drag.startX),
            h: Math.abs(point.y - drag.startY)
        };
    }

    // ── Text pins ───────────────────────────────────────────────────────────────

    protected onMouseUp(): void {
        if (this.readOnly || this.mode !== 'text') {
            return;
        }

        const selection: Selection | null = window.getSelection();
        if (!selection || selection.isCollapsed || !selection.rangeCount) {
            return;
        }

        const range: Range = selection.getRangeAt(0);
        const page: number = this.pageIndexOf(range.startContainer);
        const quote: string = selection.toString().replace(/\s+/g, ' ').trim();
        if (page < 0 || !quote) {
            return;
        }

        // A selection running onto the next page is pinned to the page it started on.
        const pageBox: DOMRect = this.pages[page].el.getBoundingClientRect();
        const rects: AdPinRect[] = this.mergeLines(
            Array.from(range.getClientRects())
                .filter((r) => r.width > 1 && r.height > 1)
                .filter((r) => r.bottom > pageBox.top && r.top < pageBox.bottom)
                .map((r) => this.normalise(page, r))
        );
        if (!rects.length) {
            return;
        }

        const startEl: Element | null = range.startContainer instanceof Element
            ? range.startContainer
            : range.startContainer.parentElement;
        const cellRef: string | undefined = startEl?.closest<HTMLElement>('td[data-cell]')?.dataset['cell'];

        this.pinDraft.emit({
            page: page + 1,
            pageLabel: this.pages[page].label,
            kind: 'text',
            rects,
            quote: quote.length > MAX_QUOTE_LENGTH ? `${quote.slice(0, MAX_QUOTE_LENGTH)}…` : quote,
            ...(cellRef ? { cellRef } : {})
        });
        selection.removeAllRanges();
    }

    /**
     * Collapses the many small boxes a selection reports — pdf.js and docx output a span per
     * text run — into one box per line.
     */
    private mergeLines(rects: AdPinRect[]): AdPinRect[] {
        const sorted: AdPinRect[] = [...rects].sort((a, b) => a.y - b.y || a.x - b.x);
        const lines: AdPinRect[] = [];

        sorted.forEach((rect) => {
            const line: AdPinRect | undefined = lines.find((l) => {
                const overlap: number = Math.min(l.y + l.h, rect.y + rect.h) - Math.max(l.y, rect.y);
                return overlap > Math.min(l.h, rect.h) * 0.5;
            });
            if (!line) {
                lines.push({ ...rect });
                return;
            }
            const right: number = Math.max(line.x + line.w, rect.x + rect.w);
            const bottom: number = Math.max(line.y + line.h, rect.y + rect.h);
            line.x = Math.min(line.x, rect.x);
            line.y = Math.min(line.y, rect.y);
            line.w = right - line.x;
            line.h = bottom - line.y;
        });

        return lines;
    }

    // ── Geometry ────────────────────────────────────────────────────────────────

    private pageIndexOf(node: Node): number {
        const el: Element | null = node instanceof Element ? node : node.parentElement;
        const pageEl: Element | null | undefined = el?.closest('.pv-page');
        return pageEl ? this.pages.findIndex((page) => page.el === pageEl) : -1;
    }

    /** A viewport point as a fraction of the page, clamped to it. */
    private pointOn(page: number, clientX: number, clientY: number): { x: number; y: number } {
        const box: DOMRect = this.pages[page].el.getBoundingClientRect();
        return {
            x: this.clamp((clientX - box.left) / box.width),
            y: this.clamp((clientY - box.top) / box.height)
        };
    }

    private normalise(page: number, rect: DOMRect): AdPinRect {
        const box: DOMRect = this.pages[page].el.getBoundingClientRect();
        const x: number = this.clamp((rect.left - box.left) / box.width);
        const y: number = this.clamp((rect.top - box.top) / box.height);
        return {
            x,
            y,
            w: this.clamp((rect.right - box.left) / box.width) - x,
            h: this.clamp((rect.bottom - box.top) / box.height) - y
        };
    }

    private clamp(value: number): number {
        return Math.min(1, Math.max(0, value));
    }
}
